import React, { useState, useEffect } from "react"
import { css, SerializedStyles, useTheme } from "@emotion/react"

import AddToCalButton from './AddToCalButton';
import Button from "./Button"
import GridContainer from "./GridContainer"
import GridCol from "./GridCol"

import {
  dropShadows,
  focusStyles,
  focusStylesDark,
  fonts,
  fontSizeLineHeight,
  mediaQueries,
  pxToRem,
  respSpacing,
  transition,
  weights,
  z
} from "../styles"

import {
  iconButtonStyles,
} from "../styles/header-styles"

import { Link } from "@gatsbyjs/reach-router"

import logoVertical from "../logos/upsun_logo-v.svg"
import logoOnlyH from "../logos/upsun-only-h.svg"
import formerlyPsh from "../logos/formerly-psh-h.svg"

import { UpsunTheme } from "../themes"

import {
  HighlightVioletBgStrong,
  HighlightVioletOnBgStrong,
  NeutralBorderDefault,
  NeutralBorderWeakDefault,
  NeutralBorderWeakDisabled,
  NeutralBgDefault,
  NeutralBgWeakDefault,
  NeutralIconActive,
  NeutralIconWeakDefault,
  NeutralTxtStrongDefault,
  NeutralTxtWeakDefault,
  NeutralTxtDefault,
  CanvasDefault,
  PrimaryBgActive,
  PrimaryTxtWeakHover,
  PrimaryTxtWeakActive,
} from "../../tokens/js/light"

import {
  NeutralBorderDefault as NeutralBorderDefaultDark,
  NeutralBorderWeakDefault as NeutralBorderWeakDefaultDark,
  NeutralBorderWeakDisabled as NeutralBorderWeakDisabledDark,
  NeutralBgDefault as NeutralBgDefaultDark,
  NeutralIconActive as NeutralIconActiveDark,
  NeutralIconWeakDefault as NeutralIconWeakDefaultDark,
  NeutralBgWeakDefault as NeutralBgWeakDefaultDark,
  NeutralTxtStrongDefault as NeutralTxtStrongDefaultDark,
  NeutralTxtWeakDefault as NeutralTxtWeakDefaultDark,
  NeutralTxtDefault as NeutralTxtDefaultDark,
  CanvasDefault as CanvasDefaultDark,
  PrimaryBgActive as PrimaryBgActiveDark,
  PrimaryTxtWeakHover as PrimaryTxtWeakHoverDark,
  PrimaryTxtWeakActive as PrimaryTxtWeakActiveDark,
} from "../../tokens/js/dark"

import { ReactComponent as HamburgerIcon } from "../icons/hamburger.svg"
import { ReactComponent as CloseIcon } from "../icons/close.svg"
import { ReactComponent as SearchIcon } from "../icons/search.svg"
import { ReactComponent as ChevronIcon } from "../icons/chevron-up-small.svg"
import { ReactComponent as ArrowIcon } from "../icons/arrow-right.svg"
import colorsLight from "../styles/colors-light";
import colorsDark from "../styles/colors-dark";
import TranslationsDropdown from "./TranslationDropdown";

type SimpleLink = {
  text: string
  href: string
  icon?: React.ReactElement
}

type ComplexLink = {
  text: string
  href: string
  desc: string
  cta?: string
  new?: boolean
}

type DropdownColumn = {
  title?: string
  links: (SimpleLink | ComplexLink)[]
}

type PrimaryHeaderDropdown = {
  text: string
  columns: DropdownColumn[]
  emphasizeFirstCol?: boolean
}

type PrimaryHeaderDropdownProps = PrimaryHeaderDropdown & {
  keyAsProp: string
  handleExpandedDropdownChange: Function
  isExpanded: boolean
  css?: SerializedStyles | SerializedStyles[]
  index: number
}

type HeaderData = {
  top: SimpleLink[]
  links: (SimpleLink | PrimaryHeaderDropdown)[]
  ctas: SimpleLink[]
  homepage?: string
}

type SimpleButton = {
  text: string
  shortText?: string
  href: string
}

type CalBtn = {
  eventTitle: string
  btnLabel?: string
  eventDesc?: string
  startDate: string
  endDate?: string
  startTime?: string
  endTime?: string
  eventLocation?: string
  timezone?: string
}

type Localization = {
  text: string
  href?: string
}

type HeaderProps = {
  data: HeaderData
  navigate?(to : any, options? : any): Promise<void>
  simpleHeader?: boolean
  simpleBtn?: SimpleButton
  calBtn?: CalBtn,
  localizations?: Localization[]
  location?: string
  css?: SerializedStyles | SerializedStyles[]
}

const PrimaryHeaderDropdown: React.FC<PrimaryHeaderDropdownProps> = ({ text, columns, emphasizeFirstCol, keyAsProp, handleExpandedDropdownChange, isExpanded, index, ...props }) => {
  const theme = useTheme() as UpsunTheme
  
  const primaryLiStyles = css`
    position: relative;

    ${mediaQueries.xl} {
      // The dropdown behavior that's accessible via button click at all bps
      // is also accessible via hover on desktop
      &:hover {
        > div { // see also: dropdownStyles
          display: flex !important;
        }

        > button { // see also: primaryButtonStyles
          color: ${theme.name === "light" ? 
            PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark
          };
          svg { // see also: chevronStyles
            transform: rotate(0deg) !important;
          }
        }
      }
    }
  `;

  const primaryButtonStyles = (isExpanded: boolean) => css`
    appearance: none;
    display: block;
    position: relative;
    width: 100%;
    border: none;
    border-bottom: solid ${pxToRem(1)} ${theme.name === 'light' ? NeutralBorderWeakDisabled : NeutralBorderWeakDisabledDark};
    padding: ${pxToRem(20)};
    background: transparent;
    text-align: left;
    font-family: ${fonts.secondary};
    font-weight: ${weights.bold};
    letter-spacing: 0.03em;
    ${fontSizeLineHeight(16, 16)}
    cursor: pointer;
    transition: ${transition()};

    color: ${theme.name === 'light' ? 
      (isExpanded ? PrimaryTxtWeakActive : NeutralTxtStrongDefault) 
      : 
      (isExpanded ? PrimaryTxtWeakActiveDark : NeutralTxtStrongDefaultDark)
    };

    ${mediaQueries.md} {
      padding: ${pxToRem(24)} ${pxToRem(32)};
    }

    ${mediaQueries.xl} {
      padding: ${pxToRem(12)} ${pxToRem(16)} ${pxToRem(12)} 0;
      line-height: 1.5;
      border-bottom: none;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      color: ${theme.name === 'light' ? PrimaryTxtWeakActive : PrimaryTxtWeakActiveDark};
    }

    &:focus-visible {
      ${theme.name === "light" ? focusStyles : focusStylesDark}
    }
  `;

  const chevronStyles = (isExpanded: boolean) => css`
    position: absolute;
    top: 50%;
    margin-top: ${pxToRem(-2)};
    margin-left: ${pxToRem(8)};
    transform: rotate(${isExpanded? '0deg' : '-180deg'});
    transition: ${transition()};

    path {
      fill: currentColor;
      transition: inherit;
    }
  `;

  const dropdownStyles = (isExpanded: boolean) => css`
    display: ${isExpanded ? 'block' : 'none'};
    padding: ${pxToRem(32)} ${pxToRem(20)};
    background: ${theme.name === 'light' ? NeutralBgDefault : NeutralBgDefaultDark};

    ${mediaQueries.md} {
      padding: ${pxToRem(40)} ${pxToRem(32)};
    }

    ${mediaQueries.xl} {
      position: absolute;
      display: ${isExpanded ? 'flex' : 'none'};
      left: 50%;
      transform: ${columns.length > 2 && index === 0 ? 'translateX(-10%)' : 'translateX(-50%)'};
      padding: 0;
      margin-top: ${pxToRem(16)};
      border: solid ${pxToRem(1)} ${theme.name === 'light' ? NeutralBorderWeakDefault : NeutralBorderWeakDefaultDark};
      border-radius: ${pxToRem(32)};
      box-shadow: ${dropShadows.medium};

      // Invisible buffer over the space between the button and dropdown
      // so that the user is still hovering over the <li> while they move
      // their mouse from the button to the dropdown.
      &::before {
        content: '';
        position: absolute;
        width: 100%;
        left: 0;
        bottom: 100%;
        height: ${pxToRem(16)};
      }      
    }
  `;

  const dropdownColStyles = (isEmphasized: boolean) => css`
    padding: ${isEmphasized ? pxToRem(32) : '0'};
    margin-bottom: ${pxToRem(40)};
    border-radius: ${isEmphasized ? pxToRem(16) : '0'};
    border: ${isEmphasized ? 
      `solid ${pxToRem(2)} ${theme.name === 'light' ? 
        NeutralBorderWeakDefault : 
        NeutralBorderWeakDefaultDark}`
      : 'none'
    };

    ${mediaQueries.xl} {
      width: ${pxToRem(270)};
      padding: ${pxToRem(40)};
      margin-bottom: 0;
      border-radius: 0;
      border: none;
      border-right: ${isEmphasized ? 
        `solid ${pxToRem(1)} ${theme.name === 'light' ? 
          NeutralBorderWeakDefault : 
          NeutralBorderWeakDefaultDark}` 
        : 'none'
      };
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  `;

  const colTitleStyles = css`
    display: block;
    ${fontSizeLineHeight(13, 20)}
    text-transform: uppercase;
    font-weight: ${weights.bold};
    letter-spacing: 0.1em;
    color: ${theme.name === 'light' ? NeutralTxtWeakDefault : NeutralTxtWeakDefaultDark};
  `;

  const colUlStyles = css`
    padding: 0;
  `;

  const simpleLiStyles = css`
    display: flex;
    align-items: center;
    margin-top: ${pxToRem(8)};

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(16)};
    }
  `;

  const simpleLinkStyles = css`
    display: flex;
    align-items: center;
    font-family: ${fonts.primary};
    ${fontSizeLineHeight(16, 32)}
    text-decoration: none;
    color: ${theme.name === 'light' ? NeutralTxtDefault : NeutralTxtDefaultDark};

    svg {
      ${respSpacing('margin-inline-end', '2xs')};
      flex: 0 0 ${pxToRem(24)};

      path {
        fill: ${theme.name === "light" ? colorsLight.PrimaryIconDefault : colorsDark.PrimaryIconDefault}
      }
    }

    &:hover,
    &:focus {
      color: ${theme.name === 'light' ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
    }
  `;

  const complexLiStyles = css`
    display: block;
    margin-top: ${pxToRem(12)};

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(16)};
    }

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(24)};
    }
  `;

  const complexLinkStyles = css`
    display: block;
    font-family: ${fonts.primary};
    text-decoration: none;
    color: ${theme.name === 'light' ? NeutralTxtDefault : NeutralTxtDefaultDark};

    &:focus-visible {
      ${theme.name === "light" ? focusStyles : focusStylesDark}
    }
  `;

  const complexLinkTitleStyles = css`
    display: block;
    ${fontSizeLineHeight(16, 24)}
    font-weight: ${weights.bold};
    transition: ${transition()};

    ${mediaQueries.xl} {
      ${fontSizeLineHeight(18, 32)}
    }

    &:hover,
    &:focus,
    &:focus-visible {
      color: ${theme.name === 'light' ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
    }
  `;

  const complexLinkDescStyles = css`
    display: block;
    margin-top: ${pxToRem(8)};
    ${fontSizeLineHeight(13, 20)}

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(12)};
    }

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(8)};
    }
  `;

  const arrowStyles = css`
    position: relative;
    top: ${pxToRem(1)};
    margin-left: ${pxToRem(8)};
    transition: ${transition()};
    color: currentColor;

    path {
      fill: currentColor;
      transition: inherit;
    }
  `;

  const complexLinkCtaStyles = css`
    display: block;
    margin-top: ${pxToRem(20)};
    ${fontSizeLineHeight(13, 20)}
    font-weight: ${weights.bold};
    transition: ${transition()};

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(24)};
    }

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(32)};
    }

    &:hover,
    &:focus {
      color: ${theme.name === 'light' ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};

      svg {
        margin-left: ${pxToRem(12)};
      }

      path {
        fill: ${theme.name === 'light' ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
      }
    }
  `;

  const newBadgeStyles = css`
    display: inline-block;
    position: relative;
    top: ${pxToRem(-3)};
    border-radius: ${pxToRem(4)};
    ${fontSizeLineHeight(10,20)}
    padding: 0 ${pxToRem(4)};
    font-family: ${fonts.secondary};
    font-weight: ${weights.medium};
    color: ${HighlightVioletOnBgStrong};
    background: ${HighlightVioletBgStrong};
    margin-left: ${pxToRem(8)};
  `;

  return (
    <li 
      css={primaryLiStyles}
      {...props}
    >
      <button
        css={primaryButtonStyles(isExpanded)}
        onClick={()=>{
          handleExpandedDropdownChange(keyAsProp)
        }}
        aria-controls={`${keyAsProp}-subnav`}
        aria-expanded={isExpanded ? 'true' : 'false'}
      >
        {text}
        <ChevronIcon css={chevronStyles(isExpanded)} />
      </button>

      <div css={dropdownStyles(isExpanded)} id={`${keyAsProp}-subnav`}>
        {columns.map((col: DropdownColumn, i: number) => {
          return (
            <div 
              css={dropdownColStyles(!i && emphasizeFirstCol === true)}
              key={`${keyAsProp}--col-${i}`}
            >
              {col.title &&
                <span css={colTitleStyles}>{col.title}</span>
              }

              {col.links && 
                <ul css={colUlStyles}>
                  {col.links.map((link: (SimpleLink | ComplexLink), j: number) => {
                    const isComplexLink = (link: SimpleLink | ComplexLink) => "desc" in link; 

                    // aka it's a SimpleLink
                    if (!isComplexLink(link)) {
                      return ( 
                        <li
                          key={`${keyAsProp}--col-${i}--link-${j}`}
                          css={simpleLiStyles}
                        >
                          <a 
                            href={link.href}
                            css={simpleLinkStyles}
                          >
                            {link.icon}
                            {link.text}
                          </a>
                        </li>
                      )
                    }

                    // Making it past the return implies 'else'
                    // aka it IS a ComplexLink
                    return (
                      <li
                        key={`${keyAsProp}--col-${i}--link-${j}`}
                        css={complexLiStyles}
                      >
                        <a 
                          href={link.href} 
                          title={link.text}
                          css={complexLinkStyles}
                        >
                          <span css={complexLinkTitleStyles}>
                            {link.text}
                            {link.new ? 
                              <span css={newBadgeStyles}>New</span>
                              : ""
                            }
                          </span>
                          <span css={complexLinkDescStyles}>{link.desc}</span>
                          {link.cta ? 
                            <span css={complexLinkCtaStyles}>
                              {link.cta}
                              <ArrowIcon css={arrowStyles} />
                            </span>
                            : ""
                          }
                        </a>
                      </li>
                    )
                  })}
                </ul>
              }
            </div>
          )
        })}
      </div>
    </li>
  )
}

const Header: React.FC<HeaderProps> = ({ data, simpleHeader, simpleBtn, calBtn, navigate, localizations, location, ...props }) => {
  const theme = useTheme() as UpsunTheme;
  const [isMobileOpen, setIsMobileOpen] = useState(false); 
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [expandedDropdown, setExpandedDropdown] = useState("");
  const [windowHeight, setWindowHeight] = useState(0);
  const [mobileDropdownHeight, setMobileDropdownHeight] = useState(0);

  // It's so gross and I'm so very sorry
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // We're going to keep re-checking this nonsense because phones think
    // it's cute to arbitrarily show/hide the bottom menu bar thingy,
    // making `100vh` unreliable
    const updateWindowHeight = () => {
      setWindowHeight(window.innerHeight);
      const bannerHeight = document.querySelector<HTMLElement>('.js-upsun-announcement')?.offsetHeight || 0;
      const headerHeight = 56; // Just how tall we happen to know it is at the breakpoints where these values matter
      setMobileDropdownHeight(window.innerHeight - headerHeight - bannerHeight);
    }

    window.addEventListener('resize', updateWindowHeight);

    if (isMobileOpen && window.innerWidth < 1280) {
      updateWindowHeight();
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    else {
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    }

    return () => window.removeEventListener('resize', updateWindowHeight);
  }, [
    isMobileOpen, 
    windowHeight, 
    setWindowHeight, 
    mobileDropdownHeight, 
    setMobileDropdownHeight
  ]);

  const headerOuterWrapperStyles = css`
    position: sticky;
    top: 0;
    z-index: ${z('header')};
    background-color: ${theme.name === "light"
      ? CanvasDefault
      : CanvasDefaultDark};
    font-family: ${fonts.secondary};
  `

  const headerGridColStyles = (isMobileOpen: Boolean) => css` // wraps it all
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    min-height: ${isMobileOpen ? pxToRem(windowHeight) : '0'};

    ${mediaQueries.xl} {
      position: relative;
      flex-direction: row;
      justify-content: space-between;
      min-height: 0;
      padding: ${simpleHeader ? `${pxToRem(24)} 0` : `${pxToRem(64)} 0 ${pxToRem(24)}`};
    }
  `;

  const simpleHeaderWrapperStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 0 0 100%;
    padding: ${pxToRem(12)} 0;

    ${mediaQueries.xl} {
      padding: 0;
    }
  `;

  const simpleLogoStyles = css`
    display: flex;

    img {
      height: ${pxToRem(18)};
      width: auto;

      ${mediaQueries.md} {
        height: ${pxToRem(24)};
      }
  
      ${mediaQueries.xl} {
        height: ${pxToRem(44)};
      }
    }
  `;

  const simpleBtnWrapper = css`
    a {
      ${fontSizeLineHeight(11,12)}
      padding: ${pxToRem(8)} ${pxToRem(16)};

      ${mediaQueries.xl} {
        ${fontSizeLineHeight(16,24)}
        padding: ${pxToRem(10)} ${pxToRem(32)};
      }
    }
  `;

  const topRowStyles = css`
    display: none;
    flex: 0 0 100%;
    gap: ${pxToRem(40)};
    justify-content: flex-end;
    padding: 0 0 ${pxToRem(16)};
    margin: 0;
    ${fontSizeLineHeight(13, 24)}
    letter-spacing: 0.03em;
    color: ${theme.name === "light"
        ? NeutralTxtDefault
        : NeutralTxtDefaultDark};

    ${mediaQueries.xl} {
      display: flex;
      position: absolute;
      top: ${pxToRem(24)};
      left: 0;
      right: 0;
    }

    li {
      display: block;
    
      &:first-of-type {
        margin-right: auto;
      }
    }

    a {
      text-decoration: none;
      background: none;
      color: inherit;
      transition: ${transition()};

      &:hover,
      &:active,
      &:focus-visible {
        color: ${theme.name === "light"
          ? PrimaryTxtWeakHover
          : PrimaryTxtWeakHoverDark};
      }

      &:focus-visible {
        ${theme.name === "light" ? focusStyles : focusStylesDark}
      }
    }
  `

  const logoAndMobileTogglesWrapperStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    ${mediaQueries.xl} {
      flex: 0 0 ${pxToRem(158)};
    }

    button {
      ${iconButtonStyles(theme.name)}
      width: ${pxToRem(56)};
      padding: ${pxToRem(16)} 0;
    }
  `

  const logoStyles = css`
    line-height: 0;

    &:focus-visible {
      ${theme.name === "light" ? focusStyles : focusStylesDark}
    }

    img {
      filter: ${theme.name === 'light' ? 'none' : 'invert(1)'};
    }
  `

  const mobileLogoStyles = css`
    display: flex;

    ${mediaQueries.xl} {
      display: none;
    }

    img {
      height: ${pxToRem(18)};
      width: auto;

      ${mediaQueries.md} {
        height: ${pxToRem(24)};
      }      
    }
  `

  const desktopLogoStyles = css`
    display: none;

    ${mediaQueries.xl} {
      display: block;
    }
  `

  const mobileTogglesWrapperStyles = css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: ${pxToRem(-16)};

    ${mediaQueries.xl} {
      display: none;
    }
  `;

  const mainMenuSlashMobileDropdownWrapperStyles = (isMobileOpen: Boolean) => css`
    display: ${isMobileOpen ? 'flex' : 'none'};
    position: relative;
    flex-direction: column;
    margin: 0 ${pxToRem(-20)};
    height: ${pxToRem(mobileDropdownHeight)};
    overflow: auto;

    ${mediaQueries.md} {
      margin: 0 ${pxToRem(-32)};
    }

    ${mediaQueries.xl} {
      display: flex;
      flex-direction: row;
      height: auto;
      margin: 0 auto 0 ${pxToRem(48)};
      align-items: stretch;
      overflow: visible;
    }
  `

  const mainLinksWrapperStyles = css`
    display: ${isSearchOpen ? 'none' : 'block'};
    padding: 0;
    margin: 0;

    ${mediaQueries.xl} {
      display: ${isSearchOpen ? 'none' : 'flex'};
      gap: ${pxToRem(48)};
    }

    a {
      transition: ${transition()};

      &:focus-visible {
        ${theme.name === "light" ? focusStyles : focusStylesDark}
      }
    }
  `

  const primaryLiStyles = css`
    display: block;
    position: relative;
  `;

  const primarySimpleLinkStyles = css`
    display: block;
    position: relative;
    width: 100%;
    border: none;
    border-bottom: solid ${pxToRem(1)} ${theme.name === 'light' ? NeutralBorderWeakDisabled : NeutralBorderWeakDisabledDark};
    padding: ${pxToRem(20)};
    font-family: ${fonts.secondary};
    font-weight: ${weights.bold};
    letter-spacing: 0.03em;
    text-decoration: none;
    ${fontSizeLineHeight(16, 16)}
    color: ${theme.name === 'light' ? NeutralTxtStrongDefault: NeutralTxtStrongDefaultDark};

    ${mediaQueries.md} {
      padding: ${pxToRem(24)} ${pxToRem(32)};
    }

    ${mediaQueries.xl} {
      padding: ${pxToRem(12)} 0;
      line-height: 1.5;
      border-bottom: none;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      color: ${theme.name === "light" ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
    }
  `;

  const topLinkMobileStyles = css`
    ${fontSizeLineHeight(13, 16)}
    letter-spacing: 0.03em;
    list-style: none;
    padding: ${pxToRem(20)};
    margin: 0;

    ${mediaQueries.md} {
      padding: ${pxToRem(24)} ${pxToRem(32)};
    }

    ${mediaQueries.xl} {
      display: none;
    }

    li + li {
      margin-top: ${pxToRem(20)};

      ${mediaQueries.md} {
        margin-top: ${pxToRem(24)};
      }
    }

    a {
      text-decoration: none;
      color: ${theme.name === "light"
        ? NeutralTxtDefault
        : NeutralTxtDefaultDark};
      background: none;

      &:hover,
      &:active {
        color: ${theme.name === "light"
          ? PrimaryTxtWeakHover
          : PrimaryTxtWeakHoverDark};
      }
    }
  `;

  const ctaMobileStyles = css`
    display: flex;
    margin-top: auto;
    gap: ${pxToRem(20)};
    padding: ${pxToRem(64)} ${pxToRem(20)} ${pxToRem(24)};

    ${mediaQueries.md} {
      gap: ${pxToRem(24)};
      padding: ${pxToRem(96)} ${pxToRem(32)} ${pxToRem(48)};
    }

    ${mediaQueries.xl} {
      display: none;
    }
  `;

  const ctaMobileBtnStyles = css`
    flex: 0 0 calc(50% - ${pxToRem(10)});

    ${mediaQueries.md} {
      flex: 0 0 calc(50% - ${pxToRem(12)});
    }
  `;

  const searchContainerStyles = css`
    ${mediaQueries.xl} {
      margin-right: ${pxToRem(16)};
      flex: ${isSearchOpen ? `0 10 calc(100% - ${pxToRem(571)})` : '0 1 auto'};
      flex: ${isSearchOpen ? `0 10 100%` : '0 1 auto'};
    }
  `

  const desktopSearchToggleStyles = css`
    display: none;

    ${mediaQueries.xl} {
      ${iconButtonStyles(theme.name)}
      display: ${isSearchOpen ? 'none' : 'block'};
      width: ${pxToRem(48)};
      height: ${pxToRem(48)};
      border-radius: ${pxToRem(8)};
      background: ${theme.name === 'light' ? NeutralBgWeakDefault : NeutralBgWeakDefaultDark};
    }
  `;

  const searchBarContainer = css`
    position: absolute;
    top: ${pxToRem(56)};
    left: 0;
    width: 100%;
    padding: ${pxToRem(10)};
    background: ${theme.name === 'light' ? PrimaryBgActive : PrimaryBgActiveDark};
    display: flex;
    align-items: center;
    justify-content: center;

    ${!isSearchOpen && `
      z-index: -9999;
      visibility: hidden;
    `}

    ${mediaQueries.xl} {
      position: relative;
      top: 0;
      left: 0;
      flex: 0 1 100%;
      padding: 0;
      background: none;

      ${!isSearchOpen && `
        position: absolute;
        visibility: hidden;
        width: 0;
        z-index: -9999;
      `}
    }
  `;

  const searchBarSubmitBtnStyles =  (searchValue: string) => css`
    ${iconButtonStyles(theme.name)}
    padding: ${pxToRem(16)};
    border-right: ${pxToRem(2)} solid ${theme.name === "light" ? NeutralBorderDefault : NeutralBorderDefaultDark};
    border-top-left-radius: ${pxToRem(8)};
    border-bottom-left-radius: ${pxToRem(8)};
    background: ${theme.name === "light" ? CanvasDefault : CanvasDefaultDark};
    cursor: ${searchValue === "" ? 'default' : 'pointer'};
    order: 1;

    ${mediaQueries.xl} {
      padding: ${pxToRem(10)} ${pxToRem(12)};
      border: ${pxToRem(2)} solid ${theme.name === "light" ? NeutralBorderDefault : NeutralBorderDefaultDark};
    }

    svg path {
      fill: ${theme.name === "light" ? 
        (searchValue === "" ? NeutralIconWeakDefault : NeutralIconActive)
        : 
        (searchValue === "" ? NeutralIconWeakDefaultDark : NeutralIconActiveDark)
      };
    }

    &:hover,
    &:focus {
      svg path {
        fill: ${theme.name === "light" ? 
          (searchValue === "" ? NeutralIconWeakDefault : PrimaryTxtWeakHover)
          : 
          (searchValue === "" ? NeutralIconWeakDefaultDark : PrimaryTxtWeakHoverDark)
        };
      }
    }

    ${!isSearchOpen && `
      display: none;
    `}
  `;

  const searchBarInputContainer = css`
    display: flex;
    width: 100%;
    position: relative;

    form {
      width: 100%;
      display: flex;
      position: relative;
    }
  `

  const searchBarInputStyles = css`
    ${fontSizeLineHeight(13,16)}
    width: 100%;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border: none;
    font-family: ${fonts.secondary};
    font-weight: ${weights.medium};
    padding: ${pxToRem(20)} ${pxToRem(80)} ${pxToRem(20)} ${pxToRem(16)};
    background: ${theme.name === "light" ? CanvasDefault : CanvasDefaultDark};
    order: 2;

    ${mediaQueries.xl} {
      max-width: 100%;
      border: ${pxToRem(2)} solid ${theme.name === "light" ? NeutralBorderDefault : NeutralBorderDefaultDark};
      border-left: none;
      padding: ${pxToRem(14)} ${pxToRem(122)} ${pxToRem(14)} ${pxToRem(16)};
    }
  `;

  const searchBarInputCloseButton = css`
    ${iconButtonStyles(theme.name)};
    display: none;

    ${mediaQueries.xl} {
      display: ${isSearchOpen ? 'initial' : 'none'};
      padding: ${pxToRem(8)};
    }
  `

  const searchBarInputButtonsContainer = css`
    position: absolute;
    display: flex;
    align-items: center;
    top: 50%;
    right: ${pxToRem(24)};
    transform: translateY(-50%);

    ${mediaQueries.xl} {
      right: ${pxToRem(8)};
      gap: ${pxToRem(2)};
    }
  `

  const searchBarInputClearButton = css`
    ${fontSizeLineHeight(13,16)}
    appearance: none;
    border: none;
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(4)} ${pxToRem(8)};
    font-family: ${fonts.secondary};
    background-color: ${theme.name === "light" ? NeutralBgWeakDefault : NeutralBgWeakDefaultDark};
    cursor: pointer;
    transition: ${transition()};
    color: ${theme.name === "light" ?  NeutralTxtStrongDefault : NeutralTxtStrongDefaultDark};

    &:hover,
    &:focus {
      color: ${theme.name === "light" ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};     
    }

    &:focus {
      ${theme.name === "light" ? focusStyles : focusStylesDark}
    }

    ${!isSearchOpen && `
      display: none;
    `}
  `

  const desktopCtaWrapperStyles = css`
    display: none;

    ${mediaQueries.xl} {
      display: flex;
      gap: ${pxToRem(16)};
    }
  `;

  const desktopCtaStyles = css`
    ${mediaQueries.xl} {
      padding-top: ${pxToRem(13)};
      padding-bottom: ${pxToRem(13)};
    }
  `;

  const clearInput = () => {
    setSearchValue("")
  }

  const handleSubmit = (e : React.FormEvent) => {
    let inputElement : HTMLInputElement | null = null;

    e.preventDefault();

    if(e.target) {
      const target = e.target as HTMLFormElement;
      inputElement = target[0] as HTMLInputElement;
    }

    if(inputElement && (typeof window !== `undefined`) && navigate) {
      navigate(`/search/?search=${inputElement?.value}`);
    }
  }

  const handleExpandedDropdownChange = (dropdownToExpand: string) => {
    if (dropdownToExpand === expandedDropdown) {
      setExpandedDropdown("");
    }
    else {
      setExpandedDropdown(dropdownToExpand);
    }
  }

  const homepage = data?.homepage || "/";

  return (
    <div css={headerOuterWrapperStyles} {...props}>
      <GridContainer>
        <GridCol colStart={1} colEnd={13} css={headerGridColStyles(isMobileOpen)}>
          
          {/* Begin simpleHeader */}
          {simpleHeader && 
            <div css={simpleHeaderWrapperStyles}>
              <div css={simpleLogoStyles}>
                <Link to={data.homepage || "/"} className="logoLink" title="Upsun" css={logoStyles}>
                  <img src={logoOnlyH} alt="" />
                </Link>
                <Link to="/platform-sh-is-now-upsun/" title="Formerly Platform.sh" css={logoStyles}>
                  <img src={formerlyPsh} alt="" />
                </Link>
              </div>

              {simpleBtn && 
               simpleBtn.text.length > 0 && 
               simpleBtn.href.length > 0 && (
                <div css={simpleBtnWrapper}>
                  <Button 
                    text={simpleBtn.text} 
                    href={simpleBtn.href} 
                    shortText={simpleBtn.shortText || ''}
                  />
                </div>
              )}

              {(calBtn && !simpleBtn) && (
                <div css={simpleBtnWrapper}>
                  <AddToCalButton
                    eventTitle={calBtn.eventTitle}
                    eventDesc={calBtn.eventDesc}
                    startDate={calBtn.startDate}
                    endDate={calBtn.endDate}
                    startTime={calBtn.startTime}
                    endTime={calBtn.endTime}
                    eventLocation={calBtn.eventLocation}
                    compressed={true}
                  />
                </div>
              )}
            </div>
          } {/* End simpleHeader */}

          {/* Begin full/default header */}
          {!simpleHeader && <>

            {/* Top row, desktop only */}
            <ul css={topRowStyles}>
              <li><a href="/platform-sh-is-now-upsun">Formerly Platform.sh</a></li>
              <TranslationsDropdown localizations={localizations || []} location={location || ""} id="translations-dropdown-desktop" />
              {data.top.map(link => {
                return (
                  <li  key={`desktop-toplink--${link.href}`}>
                    <a href={link.href}>
                      {link.text}
                    </a>
                  </li>
                )
              })}
            </ul>

            {/* Wraps logo (all bps) and mobile toggles */}
            <div css={logoAndMobileTogglesWrapperStyles}> 
              <Link to={homepage} className="logoLink" title="Upsun.com homepage" css={[logoStyles, desktopLogoStyles]}>
                <img src={logoVertical} alt="" />
              </Link>

              <div css={mobileLogoStyles}>
                <Link to={data.homepage || "/"} className="logoLink" title="Upsun" css={logoStyles}>
                  <img src={logoOnlyH} alt="" />
                </Link>
                <Link to="/platform-sh-is-now-upsun/" title="Formerly Platform.sh" css={logoStyles}>
                  <img src={formerlyPsh} alt="" />
                </Link>
              </div>

              {/* {!simpleHeader && ( // Mobile only search + menu toggles */}
              <div css={mobileTogglesWrapperStyles}>
                {/* search icon */}
                <button
                  onClick={() => {
                    setIsSearchOpen(!isSearchOpen)
                    setIsMobileOpen(false)
                  }}
                  aria-controls="search-bar-container"
                  aria-expanded={isSearchOpen ? "true" : "false"}
                  aria-label="Search"
                >
                  {!isSearchOpen ? (
                    <SearchIcon />
                  ) : (
                    <CloseIcon />
                  )}
                </button>

                {/* menu icon */}
                <button
                  onClick={() => {
                    setIsMobileOpen(!isMobileOpen)
                    setIsSearchOpen(false)
                  }}
                  aria-controls="main-nav-wrapper"
                  aria-expanded={isMobileOpen ? "true" : "false"}
                  aria-label="Menu"
                >
                  {!isMobileOpen ? (
                    <HamburgerIcon />
                  ) : (
                    <CloseIcon />
                  )}
                </button>
              </div>
              {/* )} */}
            </div>

            {/* Main menu, mobile "top" links, mobile cta buttons */}
            <div 
              css={mainMenuSlashMobileDropdownWrapperStyles(isMobileOpen)}
              id="main-nav-wrapper"
            >
              <ul css={mainLinksWrapperStyles}>
                {data.links.map((item, i: number) => {
                  const isPrimaryHeaderDropdown = (item: SimpleLink | PrimaryHeaderDropdown) => "columns" in item;

                  const keyValue = `primary-nav-item--${i}`;

                  if (!isPrimaryHeaderDropdown(item)) {
                    return  (
                      <li 
                        css={primaryLiStyles}
                        key={keyValue}
                      >
                        <a 
                          href={item.href}
                          css={primarySimpleLinkStyles}
                        >
                          {item.text}
                        </a>
                      </li>
                    )
                  } 
                  // Implied else aka it's a PrimaryHeaderDropdown
                  return (
                    <PrimaryHeaderDropdown 
                      text={item.text}
                      columns={item.columns}
                      emphasizeFirstCol={item.emphasizeFirstCol || false}
                      keyAsProp={keyValue}
                      key={keyValue}
                      isExpanded={keyValue === expandedDropdown}
                      handleExpandedDropdownChange={handleExpandedDropdownChange}
                      css={primaryLiStyles}
                      index={i}
                    />
                  )
                })}
              </ul>

              {/* Mobile "top" Links (Support, Docs, Login, etc.) */}
              <ul css={topLinkMobileStyles}>
                <TranslationsDropdown localizations={localizations || []} location={location || ""} id="translations-dropdown-mobile" />
                {data.top.map(link => {
                  return (
                    <li key={`mobile-small-link--${link.href}`}>
                      <a href={link.href} >
                        {link.text}
                      </a>
                    </li>
                  )
                })}
              </ul>

              {/* Mobile CTA buttons */}
              <div css={ctaMobileStyles}>
                {data.ctas.map((cta, index) => {
                  return (
                    <Button
                      css={ctaMobileBtnStyles}
                      href={cta.href}
                      text={cta.text}
                      key={`mobile-cta--${cta.href}`}
                      level={index ? "primary" : "secondary"}
                    />
                  )
                })}
              </div>
            </div>
         
            {/* Search functionality (mobile + desktop) */}
            <div css={searchContainerStyles}>
              {/* Desktop-only search toggle button */}
              <button
                css={desktopSearchToggleStyles}
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                aria-controls="search-bar-container"
                aria-expanded={isSearchOpen ? "true" : "false"}
                title="Search"
              >
                <SearchIcon />
              </button>

              {/* Actual search bar (desktop+mobile) */}
              <div css={searchBarContainer} id="search-bar-container">
                <div css={searchBarInputContainer}>
                  <form onSubmit={handleSubmit}>
                    <input
                      type="text"
                      css={searchBarInputStyles}
                      value={searchValue}
                      onChange={e => setSearchValue(e.target.value)}
                      autoFocus
                      aria-label="Search"
                    />
                    <button css={searchBarSubmitBtnStyles(searchValue)} aria-label="Search">
                      <SearchIcon />
                    </button>
                  </form>
                  <div css={searchBarInputButtonsContainer}>
                    {searchValue !== "" && (
                      <button
                        css={searchBarInputClearButton}
                        onClick={clearInput}
                      >
                        Clear
                      </button>
                    )}
                    <button
                      css={searchBarInputCloseButton}
                      onClick={() => setIsSearchOpen(false)}
                      aria-controls="search-bar-container"
                      aria-expanded={isSearchOpen ? "true" : "false"}
                      aria-label="Close Search"
                    >
                      <CloseIcon/>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop CTA buttons */}
            {data.ctas.length > 0 && 
              <div css={desktopCtaWrapperStyles}>
                {data.ctas.map((cta, index) => {
                  return (
                    <Button
                      href={cta.href}
                      text={cta.text}
                      key={`desktop-cta--${cta.href}`}
                      level={index ? "primary" : "secondary"}
                      css={desktopCtaStyles}
                    />
                  )
                })}
              </div>
            }
          </>}
          {/* End full/default header */}

        </GridCol>
      </GridContainer>
    </div>
  )
}

export default Header
